import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Plus, Minus, Trash2, Truck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useCart } from '@/context/CartContext';
import { formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';

/** Matches the free-delivery threshold advertised on the product detail page. */
const FREE_SHIPPING_THRESHOLD = 50;

export const CartDrawer = () => {
    const { items, isOpen, toggleCart, updateQuantity, removeItem, clearCart, cartTotal } = useCart();
    const navigate = useNavigate();

    // Guests can proceed to checkout; the account step happens there.
    const handleCheckout = () => {
        toggleCart();
        navigate('/checkout');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={toggleCart}
                        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
                    />

                    {/* Drawer */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-full sm:w-[400px] bg-white dark:bg-secondary-900 border-l border-secondary-200 dark:border-secondary-800 shadow-2xl z-50 flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-secondary-100 dark:border-secondary-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShoppingBag className="text-primary-500" size={24} />
                                <h2 className="text-lg font-bold font-display text-secondary-900 dark:text-white">
                                    Shopping Cart
                                </h2>
                                <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-bold px-2 py-1 rounded-full">
                                    {items.length} items
                                </span>
                            </div>
                            <button
                                onClick={toggleCart}
                                className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-full transition-colors text-secondary-500"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Items List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {items.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                                    <div className="w-16 h-16 bg-secondary-100 dark:bg-secondary-800 rounded-full flex items-center justify-center text-secondary-400">
                                        <ShoppingBag size={32} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-secondary-900 dark:text-white">Your cart is empty</p>
                                        <p className="text-sm text-secondary-500">Looks like you haven't added anything yet.</p>
                                    </div>
                                    <Button variant="outline" onClick={() => { toggleCart(); navigate('/products'); }}>
                                        Continue Shopping
                                    </Button>
                                </div>
                            ) : (
                                items.map((item) => {
                                    const variant = item.variantId ? item.product.variants?.find(v => v.id === item.variantId) : null;
                                    const price = variant ? parseFloat(variant.price) : parseFloat(item.product.price);
                                    const unitName = variant?.unit?.name || item.product.unit;
                                    
                                    return (
                                        <div key={`${item.product.id}-${item.variantId || 'default'}`} className="flex gap-4 p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-xl border border-secondary-100 dark:border-secondary-800">
                                            {/* Image */}
                                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-white shrink-0">
                                                <img
                                                    src={item.product.primary_image || item.product.images?.[0]?.image_url}
                                                    alt={item.product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 flex flex-col justify-between">
                                                <div>
                                                    <h4 className="font-bold text-secondary-900 dark:text-white line-clamp-1">
                                                        {item.product.name}
                                                    </h4>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[10px] uppercase font-bold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded">
                                                            {unitName}
                                                        </p>
                                                        <p className="text-xs text-secondary-500 truncate">
                                                            {item.product.farmer?.farm_name || 'Farm Product'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between mt-2">
                                                    <div className="font-bold text-primary-600 dark:text-primary-400">
                                                        {formatCurrency(price * item.quantity)}
                                                    </div>

                                                    <div className="flex items-center gap-3 bg-white dark:bg-secondary-900 rounded-lg p-1 border border-secondary-200 dark:border-secondary-700">
                                                        <button
                                                            onClick={() => {
                                                                if (item.quantity === 1) {
                                                                    removeItem(item.product.id, item.variantId);
                                                                } else {
                                                                    updateQuantity(item.product.id, item.quantity - 1, item.variantId);
                                                                }
                                                            }}
                                                            className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded transition-colors text-secondary-500"
                                                        >
                                                            {item.quantity === 1 ? <Trash2 size={14} className="text-red-500" /> : <Minus size={14} />}
                                                        </button>
                                                        <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.variantId)}
                                                            className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded transition-colors text-secondary-500"
                                                        >
                                                            <Plus size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer */}
                        {items.length > 0 && (
                            <div className="p-4 border-t border-secondary-100 dark:border-secondary-800 bg-white dark:bg-secondary-900 space-y-4">
                                {/* Free shipping progress */}
                                <div className="bg-secondary-50 dark:bg-secondary-800/60 rounded-xl p-3">
                                    {cartTotal >= FREE_SHIPPING_THRESHOLD ? (
                                        <p className="text-xs font-semibold text-green-600 dark:text-green-400 flex items-center gap-1.5">
                                            <Truck size={14} /> You've unlocked free delivery!
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-xs text-secondary-600 dark:text-secondary-300 mb-2">
                                                Add <span className="font-bold text-primary-600">{formatCurrency(FREE_SHIPPING_THRESHOLD - cartTotal)}</span> more for free delivery
                                            </p>
                                            <div className="h-1.5 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${Math.min(100, (cartTotal / FREE_SHIPPING_THRESHOLD) * 100)}%` }}
                                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                                    className="h-full bg-primary-500 rounded-full"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm text-secondary-500">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(cartTotal)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">Total</span>
                                        <span className="text-xl font-display font-bold text-secondary-900 dark:text-white">{formatCurrency(cartTotal)}</span>
                                    </div>
                                </div>
                                <Button size="lg" className="w-full gap-2" onClick={handleCheckout}>
                                    Proceed to Checkout <ArrowRight size={18} />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => {
                                        Modal.confirm({
                                            title: 'Clear Cart?',
                                            content: 'Are you sure you want to remove all items from your cart?',
                                            okText: 'Yes, Clear Cart',
                                            okType: 'danger',
                                            cancelText: 'Cancel',
                                            onOk: () => {
                                                clearCart();
                                            },
                                        });
                                    }}
                                >
                                    Clear Cart
                                </Button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};